import { ProductOrderDto, CreateProductOrderDto, CancelProductOrderDto, ProductOrderReceiptNewProductDto, CreditNoteChoiceDto, CreateProductOrderPaymentDto } from '@services/dtos'
import { baseCrudReducer, CrudState, DEFAULT_FILTERS } from './baseCrudReducer'
import { createSlice, PayloadAction, combineReducers, createAsyncThunk, createAction, AnyAction } from '@reduxjs/toolkit'
import { ProductCatalogDto } from '@services/dtos'
import { FindScheduledDeliveriesQuery, productOrderService, ProductOrdersFilters } from '@services/ProductOrderService'
import { PaginatedQueryBase } from '@services/api'
import moment from 'moment'

const name = 'productOrder'

const { reducer: crudReducer, actions, thunks } = baseCrudReducer<
  ProductOrderDto,
  CreateProductOrderDto,
  {},
  ProductOrdersFilters
>(
  name, 
  productOrderService, 
  {
    selectFilters: (state) => state.productOrder.crud.filters,
  }
)

export const upsertProductOrder = createAction<ProductOrderDto>(`${name}/upsertOne`)

const crudReducerWithUpsert = (
  state: CrudState<ProductOrderDto, ProductOrdersFilters> | undefined,
  action: AnyAction
) => {
  if (upsertProductOrder.match(action) && state) {
    return {
      ...state,
      items: {
        ...state.items,
        datas: state.items.datas.some(o => o.id === action.payload.id)
          ? state.items.datas.map(o => o.id === action.payload.id ? action.payload : o)
          : state.items.datas,
      },
    }
  }
  return crudReducer(state, action)
}

const selectedProductsSlice = createSlice({
  name: 'productOrderSelectedProducts',
  initialState: [] as ProductCatalogDto[],
  reducers: {
    setSelectedProducts: (_, action: PayloadAction<ProductCatalogDto[]>) =>
      action.payload,
    resetSelectedProducts: () => [],
  },
})

const productOrderFilesSlice = createSlice({
  name: 'productOrderFiles',
  initialState: {} as Record<string, File>,
  reducers: {
    setProductOrderFile: (
      state,
      action: PayloadAction<{ productId: string; file: File }>
    ) => {
      state[action.payload.productId] = action.payload.file
    },
    removeProductOrderFile: (
      state,
      action: PayloadAction<{ productId: string }>
    ) => {
      delete state[action.payload.productId]
    },
    resetProductOrderFiles: () => ({}),
  },
})

const productOrderReceiptFilesSlice = createSlice({
  name: 'productOrderReceiptFiles',
  initialState: {
    deliveryNote: null as File | null,
    releaseCertificate: null as File | null,
  },
  reducers: {
    setProductOrderReceiptFile: (
      state,
      action: PayloadAction<{ type: 'deliveryNote' | 'releaseCertificate'; file: File }>
    ) => {
      state[action.payload.type] = action.payload.file
    },
    removeProductOrderReceiptFile: (
      state,
      action: PayloadAction<{ type: 'deliveryNote' | 'releaseCertificate' }>
    ) => {
      state[action.payload.type] = null
    },
    resetProductOrderReceiptFiles: () => ({
      deliveryNote: null,
      releaseCertificate: null,
    }),
  },
})

const getProductOrdersToValidate = createAsyncThunk(
  `${name}/to-validate`,
  async (_, { getState }) => {
    const { filters } = (getState() as { productOrder: { validation: CrudState<ProductOrderDto, {}> } })
      .productOrder
      .validation
    const result = await productOrderService.getToValidate(filters)
    return result
  }
)

const initialValidationState: CrudState<ProductOrderDto, {}> = {
  items: { datas: [] as ProductOrderDto[], totalCount: 0 },
  filters: DEFAULT_FILTERS,
  loading: false,
}

const validationSlice = createSlice({
  name: 'validation',
  initialState: initialValidationState,
  reducers: {
    resetProductOrdersToValidate: () => initialValidationState,
    setValidationFilters: (state, action: PayloadAction<Partial<PaginatedQueryBase>>) => {
      state.filters = { ...state.filters, page: 0, ...action.payload }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProductOrdersToValidate.pending, (state) => {
        state.loading = true
      })
      .addCase(getProductOrdersToValidate.fulfilled, (state, action) => {
        state.items = action.payload
        state.loading = false
      })
      .addCase(getProductOrdersToValidate.rejected, (state) => {
        state.loading = false
      })
  },
})

const findNewProductsReceived = createAsyncThunk(
  `${name}/find-new-products-received`,
  async (id: string) => productOrderService.findNewProductsReceived(id)
)

type NewProductsReceivedState = {
  data: {
    newProduct: ProductOrderReceiptNewProductDto;
    quantity: number;
  }[]
  loading: boolean
}

const initialNewProductsReceivedState: NewProductsReceivedState = {
  data: [],
  loading: false,
}

const newProductsReceivedSlice = createSlice({
  name: 'newProductsReceived',
  initialState: initialNewProductsReceivedState,
  reducers: {
    resetNewProductsReceived: () => initialNewProductsReceivedState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(findNewProductsReceived.pending, (state) => {
        state.loading = true
      })
      .addCase(findNewProductsReceived.fulfilled, (state, action) => {
        state.data = action.payload
        state.loading = false
      })
      .addCase(findNewProductsReceived.rejected, (state, action) => {
        state.loading = false
      })
  },
})

const getScheduledDeliveries = createAsyncThunk(
  `${name}/scheduled-deliveries`,
  async (_, { getState }) => {
    const { filters } = (getState() as { productOrder: { scheduledDeliveries: CrudState<ProductOrderDto, FindScheduledDeliveriesQuery> } })
      .productOrder
      .scheduledDeliveries
    return productOrderService.findScheduledDeliveries(filters)
  }
)

const initialScheduledDeliveriesState: CrudState<ProductOrderDto, FindScheduledDeliveriesQuery> = {
  items: { datas: [] as ProductOrderDto[], totalCount: 0 },
  filters: { ...DEFAULT_FILTERS, date: moment().format('YYYY-MM-DD') },
  loading: false,
}

const scheduledDeliveriesSlice = createSlice({
  name: 'scheduledDeliveries',
  initialState: initialScheduledDeliveriesState,
  reducers: {
    resetScheduledDeliveries: () => initialScheduledDeliveriesState,
    setScheduledDeliveriesFilters: (state, action: PayloadAction<Partial<FindScheduledDeliveriesQuery>>) => {
      state.filters = { ...state.filters, page: 0, ...action.payload }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getScheduledDeliveries.pending, (state) => { state.loading = true })
      .addCase(getScheduledDeliveries.fulfilled, (state, action) => {
        state.items = action.payload
        state.loading = false
      })
      .addCase(getScheduledDeliveries.rejected, (state) => { state.loading = false })
  },
})

const acknowledgeProductOrder = createAsyncThunk(
  `${name}/acknowledge`,
  async (
    { id, file, supplierOrderNumber }: { id: string; file: File; supplierOrderNumber?: string },
    { dispatch }
  ) => {
    const result = await productOrderService.acknowledge(id, file, supplierOrderNumber)
    await dispatch(thunks.getAll())
    return result
  }
)

const cancelProductOrder = createAsyncThunk(
  `${name}/cancel`,
  async (
    { id, dto }: { id: string; dto: CancelProductOrderDto },
    { dispatch },
  ) => {
    const result = await productOrderService.cancel(id, dto)
    await dispatch(thunks.getAll())
    return result
  }
)

const markOrderAsReceived = createAsyncThunk(
  `${name}/mark-as-received`,
  async (id: string, { dispatch }) => {
    const result = await productOrderService.markOrderAsReceived(id)
    await dispatch(thunks.getAll())
    return result
  }
)

const retryFailedMailSending = createAsyncThunk(
  `${name}/retry-mail`,
  async (id: string, { dispatch }) => {
    const result = await productOrderService.retryFailedMailSending(id)
    await dispatch(thunks.getAll())
    return result
  }
)

const validateOrder = createAsyncThunk(
  `${name}/validate`,
  async (id: string, { dispatch }) => {
    const result = await productOrderService.validateOrder(id)
    await dispatch(getProductOrdersToValidate())
    await dispatch(thunks.getAll())
    return result
  }
)

const declineOrder = createAsyncThunk(
  `${name}/decline`,
  async (id: string, { dispatch }) => {
    const result = await productOrderService.declineOrder(id)
    await dispatch(getProductOrdersToValidate())
    await dispatch(thunks.getAll())
    return result
  }
)

const saveCreditNoteChoice = createAsyncThunk(
  `${name}/save-credit-note-choice`,
  async ({ id, dto }: { id: string; dto: CreditNoteChoiceDto }, { dispatch }) => {
    const result = await productOrderService.saveCreditNoteChoice(id, dto)
    await dispatch(thunks.getAll())
    return result
  }
)

const registerPayment = createAsyncThunk(
  `${name}/register-payment`,
  async ({ id, dto, file }: { id: string; dto: CreateProductOrderPaymentDto; file?: File }, { dispatch }) => {
    const result = await productOrderService.registerPayment(id, dto, file)
    await dispatch(thunks.getAll())
    return result
  }
)

const markOrderAsPaid = createAsyncThunk(
  `${name}/mark-as-paid`,
  async (id: string, { dispatch }) => {
    const result = await productOrderService.markOrderAsPaid(id)
    await dispatch(thunks.getAll())
    return result
  }
)

export const {
  setSelectedProducts,
  resetSelectedProducts,
} = selectedProductsSlice.actions
export const {
  setProductOrderFile,
  removeProductOrderFile,
  resetProductOrderFiles,
} = productOrderFilesSlice.actions
export const { resetProductOrdersToValidate, setValidationFilters } = validationSlice.actions
export const {
  setProductOrderReceiptFile,
  removeProductOrderReceiptFile,
  resetProductOrderReceiptFiles,
} = productOrderReceiptFilesSlice.actions

export default combineReducers({
  crud: crudReducerWithUpsert,
  selectedProducts: selectedProductsSlice.reducer,
  filesByProductId: productOrderFilesSlice.reducer,
  validation: validationSlice.reducer,
  productOrderReceiptFiles: productOrderReceiptFilesSlice.reducer,
  newProductsReceived: newProductsReceivedSlice.reducer,
  scheduledDeliveries: scheduledDeliveriesSlice.reducer,
})

export const { setFilters: setProductOrderFilters } = actions
export const {
  getAll: getProductOrders,
  create: createProductOrder,
} = thunks
export { 
  acknowledgeProductOrder, 
  cancelProductOrder, 
  getProductOrdersToValidate, 
  markOrderAsReceived, 
  retryFailedMailSending, 
  validateOrder,
  declineOrder,
  findNewProductsReceived,
  saveCreditNoteChoice,
  registerPayment,
  markOrderAsPaid,
}

export const { resetScheduledDeliveries, setScheduledDeliveriesFilters } = scheduledDeliveriesSlice.actions
export { getScheduledDeliveries }
